import { addThing, _deleteThing, getDefaultDate, newCPMandPOM } from "./flowActions";

import { Dispatch, GetState } from "../types/types";
import { FlowExportXandrDriverFields } from "../types/stores/flowExportXandrDriverFields";
import { FlowItem, FlowPrefixAndEntityType } from "../types/stores/flowTypes";
import { getFlowExportXandrDriverFieldsByFlowItemId } from "../reducers/flowExportXandrDriverFields";
import { XandrRateCategory } from "../types/stores/xandrRateCategory";
import { ScheduleType } from "../enums/schedules";
import { formatExpirationDate } from "../helpers/parseDate";

export const newFlowExportXandrDriverFields =
    (flowItemId: number, parentId: number, billingCategoryId?: number) =>
    (dispatch: Dispatch, getState: GetState): void => {
        const state = getState();
        const existingFields = getFlowExportXandrDriverFieldsByFlowItemId(state)[flowItemId];
        if (existingFields?.some(field => field.ParentFlowItemId === parentId)) {
            return;
        }

        const parent: FlowItem = state.flowItems.byId[parentId];
        const xandrRateCategories = state.xandrRateCategories.categories;
        const hasFieldRatePricing = state.session.enabledFeatures.includes("display-field-rates");

        // Use the same rate logic as Trade Desk and Pubmatic to ensure consistent behavior
        const maxCPM = newCPMandPOM(state, parent, flowItemId, parentId, hasFieldRatePricing, 1, false);

        if (!billingCategoryId) {
            // Searching CPM from parent filter node
            if (maxCPM !== 0) {
                const validCategories = xandrRateCategories.filter(
                    (cat): cat is XandrRateCategory => cat != null && typeof cat.category_price === "number"
                );

                // Exact match
                let matchedCategory = validCategories.find(cat => cat.category_price === maxCPM);

                // Rounding up to nearest category_price
                if (!matchedCategory) {
                    matchedCategory = validCategories
                        .filter(cat => cat.category_price >= maxCPM)
                        .sort((a, b) => a.category_price - b.category_price)[0];
                }

                // else rounding down to nearest category_price
                if (!matchedCategory) {
                    matchedCategory = validCategories
                        .filter(cat => cat.category_price < maxCPM)
                        .sort((a, b) => b.category_price - a.category_price)[0];
                }

                billingCategoryId = matchedCategory?.data_category_id ?? 0;
            }
        }

        const flowId = state.flowItems.byId[flowItemId]?.FlowId;
        const scheduleId = state.flows.byId[flowId]?.ScheduleId;
        const schedule = scheduleId
            ? (state.vars.activationDestinationSchedules || []).find(item => item.Id == scheduleId) ||
              (state.schedules?.schedules || []).find(item => item.Id == scheduleId)
            : null;
        const expirationDate =
            state.session.enabledFeatures.includes("schedule-templates") &&
            schedule &&
            !schedule.IsScheduleTemplate &&
            (schedule.ScheduleType == ScheduleType.EndDate || schedule.ScheduleType == ScheduleType.TimeFrequency)
                ? formatExpirationDate(schedule.EndDate)
                : null;

        const thing: FlowExportXandrDriverFields = {
            XandrDriverFieldId: 0,
            FlowItemId: flowItemId,
            ParentFlowItemId: parentId,
            AudienceName: parent?.FlowItemName || "",
            Description: "",
            ExpansionType: "HOUSEHOLD",
            TTL: 90,
            ExpirationDate: expirationDate || getDefaultDate(),
            BillingCategoryId: billingCategoryId ?? 0,
            DataSegementTypeId: "Audience",
        };
        dispatch(addThing("flowExportXandrDriverFields", thing));
    };

export const deleteFlowExportXandrDriverFields =
    (id: number) =>
    (dispatch: Dispatch): void => {
        dispatch(_deleteThing("flowExportXandrDriverFields", id));
    };

export const xandrDriverFieldsPrefixAndEntity: FlowPrefixAndEntityType = {
    actionPrefix: "FLOW_EXPORT_XANDR_DRIVER_FIELDS",
    stateKey: "flowExportXandrDriverFields",
    id: "XandrDriverFieldId",
    flowItemType: "FlowExportXandrDriverFields",
    deleteAc: deleteFlowExportXandrDriverFields,
};
